import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calculator, Upload, Save, Trash2, Download, FileSpreadsheet,
  AlertCircle, CheckCircle2, RefreshCw, Calendar, Target, TrendingDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Tab Overlay Semanal — replica el método del Peruano para repartir el
 * overlay de PPST entre uniones. Permite:
 *   - Editar metas (%) por unión (deben sumar 100%)
 *   - Subir el .xlsx semanal de PPPoker → procesa y muestra resultado + ratios
 *   - Descargar resultado global (xlsx) y por unión (csv)
 *
 * Doc: /app/memory/OVERLAY_METHODOLOGY.md
 */
export default function OverlayTab() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeader = { Authorization: `Bearer ${token}` };

  const [metas, setMetas] = useState([]);
  const [metasEdit, setMetasEdit] = useState(null); // null = no editando
  const [savingMetas, setSavingMetas] = useState(false);
  const [calculos, setCalculos] = useState([]);
  const [activeCalc, setActiveCalc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingMetas, setLoadingMetas] = useState(true);
  const [loadingHist, setLoadingHist] = useState(true);

  const totalPct = (metasEdit ?? metas).reduce((s, m) => s + Number(m.meta_pct || 0), 0);
  const totalPctOk = Math.abs(totalPct - 1) < 1e-6;

  const fetchMetas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/superunion/overlay/metas`, { headers: authHeader });
      setMetas(r.data.metas || []);
    } catch (e) { console.error(e); toast.error('No se pudieron cargar las metas'); }
    finally { setLoadingMetas(false); }
  }, [token]); // eslint-disable-line

  const fetchHistorico = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/superunion/overlay/calculos`, { headers: authHeader });
      setCalculos(r.data.calculos || []);
    } catch (e) { console.error(e); }
    finally { setLoadingHist(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchMetas(); fetchHistorico(); }, [fetchMetas, fetchHistorico]);

  const loadCalc = async (calcId) => {
    try {
      const r = await axios.get(`${API}/superunion/overlay/calculo/${calcId}`, { headers: authHeader });
      setActiveCalc(r.data);
    } catch (e) { toast.error('No se pudo cargar el cálculo'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('El archivo debe ser .xlsx'); return;
    }
    if (!totalPctOk) {
      toast.error('Las metas deben sumar 100% antes de cargar el Excel'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/superunion/overlay/upload`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      toast.success(`Cálculo guardado · Overlay (fichas)${r.data.totales.overlay_total.toFixed(2)}`);
      setActiveCalc(r.data);
      fetchHistorico();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Error procesando Excel: ${msg}`);
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const startEditMetas = () => setMetasEdit(JSON.parse(JSON.stringify(metas)));
  const cancelEditMetas = () => setMetasEdit(null);
  const updateMetaRow = (i, field, val) => {
    const copy = [...metasEdit];
    if (field === 'meta_pct') {
      // permitir % (ej. "59" → 0.59) o decimal (0.59)
      let n = Number(val);
      if (isNaN(n)) n = 0;
      if (n > 1) n = n / 100;
      copy[i] = { ...copy[i], meta_pct: n };
    } else {
      copy[i] = { ...copy[i], [field]: val };
    }
    setMetasEdit(copy);
  };
  const addMetaRow = () => setMetasEdit([...metasEdit, { union_id: 0, meta_pct: 0, label: '' }]);
  const removeMetaRow = (i) => setMetasEdit(metasEdit.filter((_, idx) => idx !== i));

  const saveMetas = async () => {
    if (!totalPctOk) { toast.error(`Las metas deben sumar 100% (actual: ${(totalPct*100).toFixed(4)}%)`); return; }
    setSavingMetas(true);
    try {
      const payload = { metas: metasEdit.map(m => ({ union_id: Number(m.union_id), meta_pct: Number(m.meta_pct), label: m.label || '' })) };
      await axios.put(`${API}/superunion/overlay/metas`, payload, { headers: authHeader });
      toast.success('Metas guardadas correctamente');
      setMetasEdit(null);
      fetchMetas();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`No se pudo guardar: ${msg}`);
    } finally { setSavingMetas(false); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: authHeader });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'No se pudo descargar el archivo');
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };
  const downloadGlobal = (id, semana) => downloadFile(
    `/superunion/overlay/calculo/${id}/export/global`, `overlay_${semana}_completo.xlsx`);
  const downloadUnion = (id, semana, union) => downloadFile(
    `/superunion/overlay/calculo/${id}/export/union/${union}`, `overlay_${semana}_union_${union}.csv`);

  const deleteCalc = async (calcId) => {
    if (!window.confirm('¿Eliminar este cálculo? Esta acción es irreversible.')) return;
    try {
      await axios.delete(`${API}/superunion/overlay/calculo/${calcId}`, { headers: authHeader });
      toast.success('Cálculo eliminado');
      if (activeCalc?.id === calcId) setActiveCalc(null);
      fetchHistorico();
    } catch (e) {
      toast.error(`No se pudo eliminar: ${e.response?.data?.detail || e.message}`);
    }
  };

  const view = metasEdit ?? metas;
  return (
    <div className="space-y-5" data-testid="overlay-tab">
      {/* === 1. Configuración de Metas === */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-violet-600" />
            <h3 className="text-lg font-bold">Metas por Unión</h3>
            <span className="text-xs text-gray-500">(% del GTD que cada unión debe aportar en fichas)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${totalPctOk ? 'text-emerald-600' : 'text-red-600'}`}>
              Total: {(totalPct * 100).toFixed(2)}%
              {totalPctOk ? <CheckCircle2 className="w-4 h-4 inline ml-1" /> : <AlertCircle className="w-4 h-4 inline ml-1" />}
            </span>
            {!metasEdit ? (
              <button onClick={startEditMetas} data-testid="overlay-edit-metas-btn" className="px-3 py-1.5 text-sm rounded-md border border-violet-300 text-violet-700 hover:bg-violet-50">Editar</button>
            ) : (
              <>
                <button onClick={cancelEditMetas} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancelar</button>
                <button onClick={saveMetas} disabled={!totalPctOk || savingMetas} data-testid="overlay-save-metas-btn"
                        className="px-3 py-1.5 text-sm rounded-md bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 flex items-center gap-1">
                  <Save className="w-4 h-4" /> {savingMetas ? 'Guardando…' : 'Guardar metas'}
                </button>
              </>
            )}
          </div>
        </div>

        {loadingMetas ? (
          <p className="text-sm text-gray-500">Cargando metas…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="py-1.5 px-2">Unión ID</th>
                  <th className="py-1.5 px-2">Etiqueta (opcional)</th>
                  <th className="py-1.5 px-2 text-right">Meta %</th>
                  <th className="py-1.5 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {view.map((m, i) => (
                  <tr key={`${m.union_id}-${i}`} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-1.5 px-2">
                      {metasEdit ? (
                        <input type="number" value={m.union_id} onChange={e => updateMetaRow(i, 'union_id', e.target.value)}
                               className="w-24 px-2 py-1 border rounded text-sm" data-testid={`overlay-union-id-${i}`} />
                      ) : <span className="font-mono">{m.union_id}</span>}
                    </td>
                    <td className="py-1.5 px-2">
                      {metasEdit ? (
                        <input type="text" value={m.label || ''} onChange={e => updateMetaRow(i, 'label', e.target.value)}
                               placeholder="DIVERMANIA / PERU POKER / ..."
                               className="w-64 px-2 py-1 border rounded text-sm" />
                      ) : (m.label || <span className="text-gray-400 italic">—</span>)}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      {metasEdit ? (
                        <input type="number" step="0.01" value={Number(m.meta_pct).toFixed(2) * 1}
                               onChange={e => updateMetaRow(i, 'meta_pct', e.target.value)}
                               className="w-24 px-2 py-1 border rounded text-right text-sm"
                               data-testid={`overlay-meta-pct-${i}`} />
                      ) : <span className={m.meta_pct === 0 ? 'text-gray-400' : 'font-semibold'}>{(m.meta_pct * 100).toFixed(2)}%</span>}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      {metasEdit && (
                        <button onClick={() => removeMetaRow(i)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {metasEdit && (
                  <tr>
                    <td colSpan="4" className="py-2 text-center">
                      <button onClick={addMetaRow} className="text-sm text-violet-600 hover:underline">+ Agregar unión</button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-2">
              <b>Tip</b>: las uniones con meta 0% son <b>exentas</b> (no pagan overlay). Ingresá el % como decimal (0.59) o entero (59).
            </p>
          </div>
        )}
      </div>

      {/* === 2. Importar Excel === */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="w-5 h-5 text-violet-600" />
          <h3 className="text-lg font-bold">Importar Excel PPST de la semana</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Subí el archivo que envía PPPoker por mail (ej. <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">2006-XXXXXXX-YYYYMMDD-YYYYMMDD-PPST-01.xlsx</code>). El sistema replica el método del Peruano: 1) detecta dinámicamente la columna del overlay según tipo de torneo (N/O/P), 2) calcula déficit relativo por unión, 3) reparte proporcional. El déficit debe estar informado en el Excel y el reparto debe conciliar. Si faltan datos, el cálculo no se guarda. Los archivos diarios de PPST no actualizan este historial.
        </p>
        <label data-testid="overlay-upload-btn"
               className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold cursor-pointer ${uploading ? 'bg-gray-300' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}>
          {uploading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Procesando…</> : <><FileSpreadsheet className="w-4 h-4" /> Elegir Excel</>}
          <input type="file" accept=".xlsx" className="hidden" onChange={handleUpload} disabled={uploading || !totalPctOk} />
        </label>
        {!totalPctOk && <span className="text-xs text-red-600 ml-3">⚠️ Configurá las metas (total debe ser 100%)</span>}
      </div>

      {/* === 3. Cálculo activo === */}
      {activeCalc && <CalcResult calc={activeCalc} onDownloadGlobal={downloadGlobal} onDownloadUnion={downloadUnion} onDelete={deleteCalc} />}

      {/* === 4. Histórico === */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-violet-600" />
          <h3 className="text-lg font-bold">Cálculos previos</h3>
        </div>
        {loadingHist ? <p className="text-sm text-gray-500">Cargando…</p> : calculos.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Aún no se procesó ningún Excel. Cargá el primero arriba.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="py-1.5 px-2 text-left">Semana</th>
                <th className="py-1.5 px-2 text-left">Archivo</th>
                <th className="py-1.5 px-2 text-right">Torneos</th>
                <th className="py-1.5 px-2 text-right">Overlay (fichas)</th>
                <th className="py-1.5 px-2 text-left">Subido</th>
                <th className="py-1.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {calculos.map(c => (
                <tr key={c.id} className={`border-b border-gray-100 dark:border-gray-700 hover:bg-violet-50/30 ${activeCalc?.id === c.id ? 'bg-violet-50/50' : ''}`}>
                  <td className="py-1.5 px-2 font-mono text-xs">{c.semana_id}{c.calculation_version !== 2 && <span className="block text-amber-700">Histórico sin validar</span>}</td>
                  <td className="py-1.5 px-2 text-xs text-gray-600 truncate max-w-xs">{c.archivo_origen}</td>
                  <td className="py-1.5 px-2 text-right">{c.totales?.torneos_total} <span className="text-xs text-gray-400">({c.totales?.con_overlay} c/ov)</span></td>
                  <td className="py-1.5 px-2 text-right font-semibold text-red-600">{c.totales?.overlay_total?.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-xs text-gray-500">{c.uploaded_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 px-2 text-right flex gap-1 justify-end">
                    <button onClick={() => loadCalc(c.id)} className="px-2 py-1 text-xs rounded border border-violet-300 text-violet-700 hover:bg-violet-50">Ver</button>
                    <button onClick={() => deleteCalc(c.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/** Tabla de resultado de un cálculo de overlay (por unión, con ratios) */
function CalcResult({ calc, onDownloadGlobal, onDownloadUnion, onDelete }) {
  const total = calc.totales?.overlay_total || 0;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 border border-violet-200 dark:border-violet-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2"><Calculator className="w-5 h-5 text-violet-600" /> Resultado · {calc.semana_id}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{calc.archivo_origen} · {calc.totales?.torneos_total} torneos ({calc.totales?.con_overlay} con overlay)</p>
        </div>
        <div className="flex gap-2">
          <button disabled={calc.calculation_version !== 2} onClick={() => onDownloadGlobal(calc.id, calc.semana_id)} className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1" data-testid="overlay-download-global">
            <Download className="w-4 h-4" /> Excel total
          </button>
        </div>
      </div>

      <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">Importes en fichas del reporte, sin conversión a USD. El ratio no está verificado.</p>
      {calc.calculation_version !== 2 && <div role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
        Histórico sin validar: el total y sus porcentajes pueden incluir excedentes como overlay.
        Conservamos el registro original. Volvé a importar el Excel semanal para generar un cálculo corregido; la exportación de esta versión está bloqueada.
      </div>}
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi icon={<TrendingDown className="w-4 h-4" />} label="Overlay total · fichas" value={`${total.toFixed(2)}`} sub={`semana ${calc.semana_id}`} accent="red" />
        <Kpi icon={<Target className="w-4 h-4" />} label="Torneos con overlay" value={calc.totales?.con_overlay} sub={`de ${calc.totales?.torneos_total}`} />
          <Kpi icon={<Calculator className="w-4 h-4" />} label="Cols Déficit" value={Object.entries(calc.totales?.columna_deficit_uso || {}).map(([k, v]) => `${k}:${v}`).join(' · ')} sub="N=normal, O=KO, P=SAT" />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="Uniones que pagan" value={calc.por_union?.filter(u => u.overlay_total < 0).length} sub={`de ${calc.por_union?.length}`} accent="violet" />
      </div>

      {/* Tabla por unión + ratios */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 border-b">
            <tr>
              <th className="py-2 px-2 text-left">Unión</th>
              <th className="py-2 px-2 text-right">Meta %</th>
              <th className="py-2 px-2 text-right">Meta Fichas</th>
              <th className="py-2 px-2 text-right">Alcanzado</th>
              <th className="py-2 px-2 text-right">Cumplim.</th>
              <th className="py-2 px-2 text-right">Overlay (fichas)</th>
              <th className="py-2 px-2 text-right">Particip. %</th>
              <th className="py-2 px-2 text-right" title="Overlay real / Overlay esperado por la meta. &gt;1 = pagó más de lo que correspondía">Ratio Pago/Meta</th>
              <th className="py-2 px-2 text-right" title="Overlay por unidad de meta">Overlay/Meta</th>
              <th className="py-2 px-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {calc.por_union?.map(u => {
              const r = u.ratios || {};
              const paga = u.overlay_total < 0;
              return (
                <tr key={u.union_id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="py-1.5 px-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${paga ? 'bg-red-500' : 'bg-gray-300'}`} />
                      <span className="font-mono font-semibold">{u.union_id}</span>
                      {u.label && <span className="text-xs text-gray-500">· {u.label}</span>}
                      {u.meta_pct === 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">EXENTA</span>}
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-right">{(u.meta_pct * 100).toFixed(2)}%</td>
                  <td className="py-1.5 px-2 text-right text-xs text-gray-600">{u.meta_fichas_total.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-right text-xs text-gray-600">{u.alcanzado_fichas_total.toFixed(2)}</td>
                  <td className={`py-1.5 px-2 text-right text-xs ${r.cumplimiento_pct >= 100 ? 'text-emerald-600 font-semibold' : 'text-amber-600'}`}>
                    {r.cumplimiento_pct?.toFixed(1)}%
                  </td>
                  <td className={`py-1.5 px-2 text-right font-semibold ${paga ? 'text-red-600' : 'text-gray-400'}`}>
                    {u.overlay_total.toFixed(2)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-xs">{r.participacion_pct?.toFixed(2)}%</td>
                  <td className={`py-1.5 px-2 text-right text-xs ${(r.ratio_pago_vs_meta || 0) > 1 ? 'text-red-600 font-semibold' : 'text-emerald-600'}`}>
                    {(r.ratio_pago_vs_meta || 0).toFixed(3)}x
                  </td>
                  <td className="py-1.5 px-2 text-right text-xs text-gray-600">{(r.overlay_per_dollar_meta || 0).toFixed(4)}</td>
                  <td className="py-1.5 px-2 text-right">
                    {paga && (
                      <button disabled={calc.calculation_version !== 2} onClick={() => onDownloadUnion(calc.id, calc.semana_id, u.union_id)}
                              data-testid={`overlay-download-union-${u.union_id}`}
                              className="p-1.5 rounded text-violet-600 hover:bg-violet-50" title="Descargar CSV de esta unión">
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-gray-50 dark:bg-gray-800/60 font-bold border-t-2">
              <td className="py-2 px-2">TOTAL</td>
              <td className="py-2 px-2 text-right">100.00%</td>
              <td colSpan="3"></td>
              <td className="py-2 px-2 text-right text-red-600">{total.toFixed(2)}</td>
              <td className="py-2 px-2 text-right">100.00%</td>
              <td colSpan="3"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        <b>Ratios</b>: <b>Cumplim.</b> = % de la meta alcanzado en fichas (≥100% = unión cumplió). <b>Particip. %</b> = porción del overlay total que asume. <b>Ratio Pago/Meta</b> = overlay real / overlay esperado por la meta (&gt;1 = pagó más de lo que le tocaba por meta porque no cumplió). <b>Overlay/Meta</b> = overlay por unidad de meta.
      </p>
      <div className="mt-3 flex gap-2">
        <button onClick={() => onDelete(calc.id)} className="text-xs text-red-600 hover:underline">Eliminar este cálculo</button>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent }) {
  const color = accent === 'red' ? 'border-red-200 bg-red-50/40 text-red-700'
    : accent === 'violet' ? 'border-violet-200 bg-violet-50/40 text-violet-700'
    : 'border-gray-200 bg-gray-50/40 text-gray-700';
  return (
    <div className={`rounded-lg border ${color} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider flex items-center gap-1 opacity-80">{icon} {label}</div>
      <div className="text-xl font-bold leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
